import { useEffect, useState } from 'react'
import { doc, getDoc } from 'firebase/firestore'
import { db } from '../data'
import { defaultOrderAdjustments, resolveOrderAdjustments } from '../utils/orderAdjustments'

export const DEFAULT_COMMERCE_ID = 'goldmund'

const fallbackCommerceConfigs = {
    goldmund: {
        id: 'goldmund',
        displayName: 'Goldmund',
        legalName: 'GOLDMUND S.A.',
        taxId: '30-70860387-9',
        warehouseCode: '120',
        documentLabel: 'Proforma',
        documentPrefix: 'P-',
        primaryLogoKey: 'peabody',
        secondaryLogoKey: 'goldmund',
        primaryLogoUrl: '',
        secondaryLogoUrl: '',
        bankAccounts: [
            { bco: 'NACION', cc: '7240028043', cbu: '0110724320072400280434' },
        ],
        proformaValidityNote: 'Vigencia de la proforma: 72hs habiles. La presente no compromete stock ni reserva unidades, puede variar segun disponibilidad de la empresa.',
        legalNote: 'EL VALOR EXPRESADO EN LA NOTA DE PEDIDO ES CON IVA. GOLDMUND S.A ES AGENTE DE PERCEPCION DE INGRESOS BRUTOS EN LA PROV DE BS.AS. Y C.A.B.A, POR LO QUE SE INCLUIRA LA ALICUOTA CORRESPONDIENTE EN CADA FACTURA, EL CUAL DEBERA SER ABONADA AL MOMENTO DE EMISION.',
        conditionsNote: 'LAS CONDICIONES PRESENTES PUEDEN CAMBIAR ANTE FLUCTUACIONES DE CONTEXTO. LISTA DE PRECIOS SUJETA A MODIFICACION SIN PREVIO AVISO Y A EVALUACION COMERCIAL.',
        orderFormConfig: {
            requiresFinalValidation: false,
            orderAdjustments: defaultOrderAdjustments,
            mupCategories: [
                { value: 'Calef', label: 'Calef' },
                { value: 'Venti', label: 'Venti' },
                { value: 'Pad', label: 'Pad' },
            ],
            summary: {
                ivaLabel: 'IVA 21%',
            },
        },
    },
    escorial: {
        id: 'escorial',
        displayName: 'Escorial',
        legalName: 'ESCORIAL S.A.',
        taxId: '',
        warehouseCode: '',
        documentLabel: 'Proforma',
        documentPrefix: 'P-',
        primaryLogoKey: 'peabody',
        secondaryLogoKey: '',
        primaryLogoUrl: '',
        secondaryLogoUrl: '',
        bankAccounts: [],
        proformaValidityNote: 'Vigencia de la proforma: 72hs habiles. La presente no compromete stock ni reserva unidades, puede variar segun disponibilidad de la empresa.',
        legalNote: 'Completar desde la coleccion Comercios en Firebase con la leyenda legal e impositiva de Escorial.',
        conditionsNote: 'LAS CONDICIONES PRESENTES PUEDEN CAMBIAR ANTE FLUCTUACIONES DE CONTEXTO. LISTA DE PRECIOS SUJETA A MODIFICACION SIN PREVIO AVISO Y A EVALUACION COMERCIAL.',
        orderFormConfig: {
            requiresFinalValidation: false,
            orderAdjustments: defaultOrderAdjustments,
            mupCategories: [
                { value: 'Calef', label: 'Calef' },
                { value: 'Venti', label: 'Venti' },
                { value: 'Pad', label: 'Pad' },
            ],
            summary: {
                ivaLabel: 'IVA 21%',
            },
        },
    }
}

const normalizeCommerceConfig = (commerceId, remoteConfig) => {
    const fallback = fallbackCommerceConfigs[commerceId] || fallbackCommerceConfigs[DEFAULT_COMMERCE_ID]
    const config = remoteConfig || {}

    return {
        ...fallback,
        ...config,
        id: commerceId || fallback.id,
        bankAccounts: Array.isArray(config.bankAccounts) ? config.bankAccounts : fallback.bankAccounts,
        documentLabel: config.documentLabel || fallback.documentLabel,
        documentPrefix: config.documentPrefix ?? fallback.documentPrefix,
        primaryLogoKey: config.primaryLogoKey || fallback.primaryLogoKey,
        secondaryLogoKey: config.secondaryLogoKey ?? fallback.secondaryLogoKey,
        primaryLogoUrl: config.primaryLogoUrl ?? fallback.primaryLogoUrl,
        secondaryLogoUrl: config.secondaryLogoUrl ?? fallback.secondaryLogoUrl,
        orderFormConfig: {
            ...fallback.orderFormConfig,
            ...(config.orderFormConfig || {}),
            requiresFinalValidation: typeof config.orderFormConfig?.requiresFinalValidation === 'boolean'
                ? config.orderFormConfig.requiresFinalValidation
                : fallback.orderFormConfig.requiresFinalValidation,
            orderAdjustments: resolveOrderAdjustments({
                ...fallback.orderFormConfig,
                ...(config.orderFormConfig || {}),
            }),
            mupCategories: Array.isArray(config.orderFormConfig?.mupCategories)
                ? config.orderFormConfig.mupCategories
                : fallback.orderFormConfig.mupCategories,
            summary: {
                ...fallback.orderFormConfig.summary,
                ...(config.orderFormConfig?.summary || {}),
            },
        },
    }
}

export const useCommerceConfig = (commerceId) => {
    const normalizedCommerceId = commerceId || DEFAULT_COMMERCE_ID
    const [commerceConfig, setCommerceConfig] = useState(
        normalizeCommerceConfig(normalizedCommerceId)
    )

    useEffect(() => {
        let isMounted = true

        const fetchCommerceConfig = async () => {
            setCommerceConfig(normalizeCommerceConfig(normalizedCommerceId))

            try {
                const commerceRef = doc(db, 'Comercios', normalizedCommerceId)
                const commerceSnap = await getDoc(commerceRef)

                if (isMounted && commerceSnap.exists()) {
                    setCommerceConfig(
                        normalizeCommerceConfig(normalizedCommerceId, commerceSnap.data())
                    )
                }
            } catch (error) {
                if (isMounted) {
                    setCommerceConfig(normalizeCommerceConfig(normalizedCommerceId))
                }
            }
        }

        fetchCommerceConfig()

        return () => {
            isMounted = false
        }
    }, [normalizedCommerceId])

    return commerceConfig
}
